import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Notification } from '@task-bazaar/shared'

// ─── Store 接口 ─────────────────────────────────────────────

interface NotificationState {
  notifications: Notification[]
  unreadCount: number
  total: number
  page: number
  pageSize: number

  // Actions
  startPolling: () => void
  stopPolling: () => void
  loadNotifications: (page?: number) => Promise<void>
  loadUnreadCount: () => Promise<void>
  markRead: (id: string) => void
  markAllRead: () => void
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'isRead'>) => void
}

// ─── 获取 Token ─────────────────────────────────────────────

function getToken(): string | null {
  try {
    const authStorage = localStorage.getItem('auth-storage')
    if (authStorage) {
      const parsed = JSON.parse(authStorage)
      return parsed.state?.token || null
    }
  } catch {}
  return null
}

// ─── 轮询定时器 ─────────────────────────────────────────────

let pollTimer: ReturnType<typeof setInterval> | null = null

// ─── Store ──────────────────────────────────────────────────

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      unreadCount: 0,
      total: 0,
      page: 1,
      pageSize: 10,

      // 启动轮询
      startPolling: () => {
        if (pollTimer) return
        get().loadUnreadCount()
        pollTimer = setInterval(() => {
          get().loadUnreadCount()
        }, 30 * 1000)
      },

      // 停止轮询
      stopPolling: () => {
        if (pollTimer) {
          clearInterval(pollTimer)
          pollTimer = null
        }
      },

      loadNotifications: async (page?: number) => {
        const token = getToken()
        if (!token) return

        const currentPage = page || 1
        const { pageSize } = get()

        try {
          const response = await fetch(`/api/notifications?page=${currentPage}&pageSize=${pageSize}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          const data = await response.json()
          if (data.code === 0) {
            set({
              notifications: data.data.list,
              total: data.data.total,
              page: currentPage,
            })
          }
        } catch (error) {
          console.error('Failed to load notifications:', error)
        }
      },

      loadUnreadCount: async () => {
        const token = getToken()
        if (!token) return

        try {
          const response = await fetch('/api/notifications/unread-count', {
            headers: { Authorization: `Bearer ${token}` },
          })
          const data = await response.json()
          if (data.code === 0) {
            set({ unreadCount: data.data.count })
          }
        } catch (error) {
          console.error('Failed to load unread count:', error)
        }
      },

      markRead: async (id: string) => {
        const token = getToken()
        if (!token) return

        try {
          await fetch(`/api/notifications/${id}/read`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}` },
          })

          const { notifications } = get()
          const updated = notifications.map((n) =>
            n.id === id ? { ...n, isRead: true } : n
          )
          const unreadCount = updated.filter((n) => !n.isRead).length
          set({ notifications: updated, unreadCount })
        } catch (error) {
          console.error('Failed to mark notification as read:', error)
        }
      },

      markAllRead: async () => {
        const token = getToken()
        if (!token) return

        try {
          await fetch('/api/notifications/read-all', {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}` },
          })

          const { notifications } = get()
          const updated = notifications.map((n) => ({ ...n, isRead: true }))
          set({ notifications: updated, unreadCount: 0 })
        } catch (error) {
          console.error('Failed to mark all notifications as read:', error)
        }
      },

      addNotification: (notification) => {
        const { notifications } = get()
        const newNotification: Notification = {
          ...notification,
          id: `notif_${Date.now()}`,
          isRead: false,
          createdAt: new Date().toISOString(),
        }
        const updated = [newNotification, ...notifications]
        const unreadCount = updated.filter((n) => !n.isRead).length
        set({ notifications: updated, unreadCount })
      },
    }),
    {
      name: 'notification-storage',
      partialize: (state) => ({
        notifications: state.notifications,
        unreadCount: state.unreadCount,
        total: state.total,
        page: state.page,
        pageSize: state.pageSize,
      }),
    },
  ),
)
